import { Calendar } from "@/components/ui/calendar"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { MAX_500_CHARACTERS } from "@/typings/formik"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { Button } from "@/components/ui/baseUi/button"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { type Objective, type ObjectiveDraft } from "@modules/objectives/objectivesTypes"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { displayedFullDateFormat } from "@modules/utils/dateUtils"
import { capitalize } from "@modules/utils/stringUtils"
import dayjs from "dayjs"
import { CalendarIcon } from "lucide-react"
import { z } from "zod"

const objectivesSchema = (_language: string) =>
  z.object({
    id: z.string(),
    objective: z
      .string()
      .min(1, word("global.form.required"))
      .max(MAX_500_CHARACTERS, word("form.maximum.characters", { count: MAX_500_CHARACTERS })),
    targetDate: z.date().refine((d) => !!d, word("global.form.required")),
  })

type ObjectiveFormFields = z.infer<typeof objectivesSchema>

type ObjectivesPopupProps = {
  hideObjectivesPopup: () => void
  objectivePrimary: Objective | null
} & React.ComponentProps<"div">

export function ObjectivesPopup({ hideObjectivesPopup, objectivePrimary, ...props }: ObjectivesPopupProps) {
  const { objectivesService } = useServicesContext()
  const confirmPopup = useConfirmPopup()
  const language = useLanguage()

  const form = useAppForm({
    defaultValues: {
      id: objectivePrimary?.id ?? "",
      objective: objectivePrimary?.objective ?? "",
      targetDate: objectivePrimary?.targetDate ? dayjs(objectivePrimary.targetDate).toDate() : new Date(),
    } satisfies ObjectiveFormFields,
    validators: {
      onSubmit: objectivesSchema(language),
      onBlur: objectivesSchema(language),
    },
    onSubmit: async ({ value }) => {
      const draft: ObjectiveDraft = {
        id: value.id,
        objective: value.objective,
        targetDate: dayjs(value.targetDate).format("YYYY-MM-DD"),
      }
      if (value.id) {
        try {
          await objectivesService.updateOjective(draft)
          toast.success(word("dashboard.objectives.addGoalToast.success"), {
            classNames: successToastClassName(),
          })
          hideObjectivesPopup()
        } catch (e: unknown) {
          console.error("Error on update objective", e)
          toast.error(word("global.error.label"), {
            description: e instanceof Error ? e.message : String(e),
            classNames: errorToastClassName(),
          })
        }
      } else {
        try {
          await objectivesService.createNewObjective(draft)
          toast.success(word("dashboard.objectives.addGoalToast.success"), {
            classNames: successToastClassName(),
          })
          hideObjectivesPopup()
        } catch (e: unknown) {
          console.error("Error on create objective", e)
          toast.error(word("global.error.label"), {
            description: e instanceof Error ? e.message : String(e),
            classNames: errorToastClassName(),
          })
        }
      }
    },
  })

  const deleteObjective = (id: string) => {
    confirmPopup.show({
      title: word("dashboard.objectives.removeGoal.popup.title"),
      message: word("dashboard.objectives.removeGoal.popup.message"),
      accept: async () => {
        await objectivesService.deleteObjective(id)
        hideObjectivesPopup()
        toast.success(word("dashboard.objectives.removeGoalToast.success"), {
          classNames: successToastClassName(),
        })
      },
    })
  }

  return (
    <div {...props} className="flex flex-col gap-4 py-6">
      <form.AppField name="targetDate">
        {(field) => (
          <field.Field>
            <field.Label required>{word("dashboard.objectives.deadline")}</field.Label>
            <field.Content>
              <Popover>
                <PopoverTrigger
                  render={
                    <Button
                      variant="outline"
                      className="flex h-11 w-full text-left font-normal whitespace-normal hover:border hover:border-borderPrimary hover:[&>svg]:text-primary-500 hover:[&>svg]:opacity-100"
                    >
                      {capitalize(dayjs(field.state.value).format(displayedFullDateFormat))}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  }
                />
                <PopoverContent className="w-auto border-0 p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.state.value}
                    captionLayout="dropdown"
                    disabled={(date) => date > dayjs().add(100, "year").toDate() || date < dayjs().toDate()}
                    onSelect={(date) => {
                      if (date) field.handleChange(date)
                    }}
                  />
                </PopoverContent>
              </Popover>
              <field.Message />
            </field.Content>
          </field.Field>
        )}
      </form.AppField>

      <form.AppField
        name="objective"
        validators={{
          onChange: objectivesSchema(language).shape.objective,
        }}
      >
        {(field) => (
          <field.Field>
            <field.Label required>{word("dashboard.objectives.title")}</field.Label>
            <field.Content>
              <field.Textarea
                placeholder={word("dashboard.objectives.title")}
                className="field-sizing-fixed"
                rows={5}
              />
              <field.Message />
            </field.Content>
          </field.Field>
        )}
      </form.AppField>

      <div className="flex justify-end gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (objectivePrimary) deleteObjective(objectivePrimary.id)
          }}
          disabled={!objectivePrimary}
        >
          {word("global.delete")}
        </Button>
        <form.Subscribe selector={(s) => [s.isSubmitting, s.canSubmit]}>
          {([isSubmitting, canSubmit]) => (
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={!canSubmit || isSubmitting}
              isLoading={isSubmitting}
              onClick={() => void form.handleSubmit()}
            >
              {word("global.save")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </div>
  )
}
